import { createProcurementOrder } from '@api/procurement-order';
import { listProductsForVendor } from '@api/vendors';
import type { InventoryHealthRow } from '@api/replenishment-planning';
import useToaster from '@hooks/toaster/useToaster';
import {
    Button,
    Group,
    Modal,
    NumberInput,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { useEffect, useState, type FC } from 'react';

type Props = {
    opened: boolean;
    /**
     * Rows already narrowed to ONE supplier — the pivot guarantees that.
     *
     * The reorder point is omitted deliberately: a group row carries none (a threshold does
     * not roll up), and nothing here reads it — the order is seeded from `shortfall`.
     */
    rows: Array<Omit<InventoryHealthRow, 'reorderPoint'>>;
    vendorId: string | null;
    vendorName: string | null;
    onClose: () => void;
    onCreated: () => void;
};

/**
 * `productId` is what the operator sees (matching the grid's identifier column); `sku` is
 * what goes on the PO line the VENDOR reads. Deliberately two fields — our internal
 * product id is not a useful thing to print on someone else's purchase order.
 */
type LineDraft = {
    productId: string;
    sku: string | null;
    quantity: number;
    /** Shown next to the input so the buyer can see what they must clear. */
    moq: number | null;
    /** Material rows only — quantities are not unit-uniform across materials. */
    unit: string | null;
};

/**
 * Places a DRAFT purchase order straight from the reorder-point view — no plan, no
 * requirement graph, no approval chain. Quantities seed from each SKU's shortfall and
 * stay editable, because the whole premise of this surface is that the user decides how
 * much to buy and when.
 */
const CreatePoFromShortfallModal: FC<Props> = ({
    opened,
    rows,
    vendorId,
    vendorName,
    onClose,
    onCreated,
}) => {
    const { showToastSuccess, showToastError } = useToaster();
    const [lines, setLines] = useState<LineDraft[]>([]);
    const [poNumber, setPoNumber] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!opened) return;
        // Every row is listed, not just the short ones. Seeding from shortfall is a
        // convenience, not a gate: materials carry no reorder point (their need comes from
        // the BOM explosion, not their own demand), so filtering on shortfall > 0 would
        // render an empty modal for the entire material view.
        setLines(
            rows.map((row) => ({
                productId: row.productId,
                sku: row.sku,
                quantity: Math.max(0, row.shortfall),
                moq: row.moq,
                unit: row.unit,
            })),
        );
        setPoNumber('');
    }, [opened, rows]);

    const totalUnits = lines.reduce((sum, line) => sum + (line.quantity || 0), 0);

    const handleCreate = async () => {
        const payload = lines.filter((line) => line.quantity > 0);
        if (payload.length === 0) return;
        setBusy(true);
        try {
            // The vendor's agreed prices, where they exist; a line without one keeps 0 for
            // the form to fill, as before.
            const agreed = new Map<string, number>();
            if (vendorId) {
                try {
                    for (const p of await listProductsForVendor(vendorId)) {
                        if (p.price != null) agreed.set(p.productId, p.price);
                    }
                } catch {
                    /* prices unavailable - the draft is still worth creating */
                }
            }
            await createProcurementOrder({
                status: 'DRAFT',
                poNumber: poNumber.trim() || null,
                vendorId,
                vendorName,
                items: payload.map((line) => ({
                    // Vendor-facing text — the SKU they recognise, falling back to the
                    // product id only when the SKU is missing.
                    description: line.sku ?? line.productId,
                    productId: line.productId,
                    quantity: line.quantity,
                    unitPrice: agreed.get(line.productId) ?? 0,
                })),
            });
            showToastSuccess({
                title: `Draft PO created for ${payload.length} SKU(s)`,
            });
            onCreated();
            onClose();
        } catch (error) {
            showToastError({
                title: 'Could not create the PO',
                subtitle: error instanceof Error ? error.message : undefined,
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size="lg"
            title={
                <Stack gap={2}>
                    <Text fw={600} size="sm">
                        Create PO — {vendorName ?? 'Unassigned supplier'}
                    </Text>
                    <Text size="xs" c="dimmed">
                        {lines.length} SKU(s) · {totalUnits} units · saved as a draft
                    </Text>
                </Stack>
            }
        >
            <Stack gap="md">
                <TextInput
                    label="PO number"
                    placeholder="Leave blank to auto-generate"
                    size="xs"
                    value={poNumber}
                    onChange={(event) => setPoNumber(event.currentTarget.value)}
                />

                {/* An editable draft list, not a data grid — DataSheet is for read surfaces,
                    so this stays a plain stack of rows. */}
                <Stack gap="xs">
                    {lines.map((line, index) => (
                        <Group key={line.productId} justify="space-between" wrap="nowrap">
                            <Stack gap={0}>
                                <Text size="xs" lineClamp={1}>
                                    {line.productId}
                                </Text>
                                {(line.moq != null || line.unit) && (
                                    <Text
                                        size="10px"
                                        c={
                                            line.moq != null && line.quantity < line.moq
                                                ? 'orange'
                                                : 'dimmed'
                                        }
                                    >
                                        {[
                                            line.moq != null ? `MOQ ${line.moq}` : null,
                                            line.unit,
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </Text>
                                )}
                            </Stack>
                            <NumberInput
                                size="xs"
                                w={140}
                                min={0}
                                value={line.quantity}
                                onChange={(value) =>
                                    setLines((prev) =>
                                        prev.map((l, i) =>
                                            i === index
                                                ? { ...l, quantity: Number(value) || 0 }
                                                : l,
                                        ),
                                    )
                                }
                            />
                        </Group>
                    ))}
                </Stack>

                {lines.length === 0 && (
                    <Text size="sm" c="dimmed">
                        Nothing below its reorder point for this supplier.
                    </Text>
                )}

                <Group justify="flex-end">
                    <Button variant="default" size="xs" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        size="xs"
                        loading={busy}
                        disabled={totalUnits <= 0}
                        onClick={handleCreate}
                    >
                        Create draft PO
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default CreatePoFromShortfallModal;
